/* eslint-disable no-alert */
import React from 'react';
import type { Blocker, BlockerFunction, Location, NavigationType } from 'react-router';
import { useBlocker } from 'react-router';

import type { MayBe } from 'modules/global/typing/common/common';

type PromptMessage = string | ((location: Location, action?: NavigationType) => string | boolean);

export const usePrompt = (message: PromptMessage, when = true): void => {
  const historyActionRef = React.useRef<NavigationType | undefined>(undefined);

  const shouldBlock = React.useCallback<BlockerFunction>(
    ({ historyAction }) => {
      historyActionRef.current = historyAction;
      return when;
    },
    [when]
  );

  const blocker = useBlocker(shouldBlock);
  // The router publishes its state inside a transition, so an urgent re-render (a new `message`) can
  // still read a `blocked` blocker that was already answered. Answering it again calls a stale
  // `proceed()`, which the router rejects ("Invalid blocker state transition: unblocked -> proceeding").
  // Each blocked navigation is a new blocker object: answer each one once.
  const answeredBlockerRef = React.useRef<MayBe<Blocker>>(undefined);

  React.useEffect(() => {
    if (blocker.state !== 'blocked' || answeredBlockerRef.current === blocker) {
      return;
    }
    answeredBlockerRef.current = blocker;

    let response: string | boolean;
    if (typeof message === 'function') {
      response = message(blocker.location, historyActionRef.current);
      if (typeof response === 'string') {
        response = window.confirm(response);
      }
    } else {
      response = window.confirm(message);
    }

    if (response) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, message]);
};
